import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import { displayDigits } from '../../utils/displayDigits';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CalendarGridItem } from '../../hooks/useCalendarData';
import { jalaliDateKey, jalaliMonths, jalaliToGregorian, formatGregorian } from '../../utils/jalali';
import { formatIsoDateForCalendarDayHeader, isJalaliPickerLocale } from '../../utils/calendarPickerLocale';
import { useLocale } from '../../hooks/useLocale';
import type { CalendarWorkout } from '../../services/api';
import type { CalendarFileDropResult } from '../../utils/calendarFileDrop';
import { calendarActions, forms, healthDocuments } from '../../services/api';
import { NmDayWorkoutList, type NmDayWorkoutItem } from '../../components/NmDayWorkoutList';
import { WorkoutStructureBarMini } from '../../components/WorkoutStructureBarMini';
import { useConfirm } from '../../components/NmConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { buildWorkoutRowMetrics } from './calendarHelpers';
import {
  getWorkoutColorSelect,
  getWorkoutTssTotal,
} from './calendarHelpers';
import { CalendarReadinessWidget } from './CalendarReadinessWidget';
import { CalendarMetricRow } from './CalendarMetricRow';
import { CalendarEventRow } from './CalendarEventRow';
import { CalendarNoteRow } from './CalendarNoteRow';
import { CalendarHealthDocumentRow } from '../../components/calendar/CalendarHealthDocumentRow';
import {
  CalendarItemActionSheet,
  type CalendarActionTarget,
} from './CalendarItemActionSheet';
import { CalendarDayAddButton } from './CalendarDayAddButton';
import { CalendarUploadOverlay } from '../../components/CalendarUploadOverlay';
import { useCalendarDayFileDrop } from '../../hooks/useCalendarDayFileDrop';
import { isOsFileDrag } from '../../utils/calendarFileDrop';
import { useCalendarItemMove } from '../../hooks/useCalendarItemMove';
import { useCalendarItemDragDrop } from '../../hooks/useCalendarItemDragDrop';
import { isGregorianDateToday } from './calendarDayAddUtils';

interface Props {
  item: CalendarGridItem;
  userId?: number | null;
  onWorkoutPress?: (workout: CalendarWorkout) => void;
  onReload?: () => void | Promise<void>;
  onUploadComplete?: (result: CalendarFileDropResult) => void | Promise<void>;
  onMetricMoved?: (targetDateIso: string) => void | Promise<void>;
}

function gregorianDateParam(item: CalendarGridItem): string {
  const g = jalaliToGregorian(item.year, item.month, Number(item.day));
  return formatGregorian(new Date(g.year, g.month - 1, g.day));
}

/** Day detail row — mirrors Ai-Tempo CalendarDayRow layout. */
export function CalendarDayRow({ item, userId, onWorkoutPress, onReload, onUploadComplete, onMetricMoved }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [actionTarget, setActionTarget] = useState<CalendarActionTarget | null>(null);

  const extra = item.extra;
  const dateKey = jalaliDateKey(item.year, item.month, item.day);
  const testDateHint = gregorianDateParam(item);

  const fileDrop = useCalendarDayFileDrop({
    userId,
    testDateHint,
    onComplete: onUploadComplete,
  });

  const dateParam = gregorianDateParam(item);
  const isToday = isGregorianDateToday(dateParam);
  const dayHeaderLabel = formatIsoDateForCalendarDayHeader(dateParam, locale);
  const gregorianDate = new Date(`${dateParam}T12:00:00`);
  const timelineDay = createDisplayNumberFormat(locale, { useGrouping: false }).format(
    isJalaliPickerLocale(locale) ? Number(item.day) : gregorianDate.getDate(),
  );
  const timelineWeekday = new Intl.DateTimeFormat(locale, {
    weekday: isJalaliPickerLocale(locale) ? 'narrow' : 'short',
  }).format(gregorianDate);
  const timelineMarker = (
    <span className="calendar-day-row__timeline-date" aria-hidden="true">
      <strong>{timelineDay}</strong>
      <small>{timelineWeekday}</small>
    </span>
  );

  const { requestMove, movePicker, moveItemToDate } = useCalendarItemMove({
    userId,
    onReload,
    onMoved: onMetricMoved,
    showToast,
  });

  const itemDrag = useCalendarItemDragDrop({ userId, moveItemToDate });
  const metricDropProps = itemDrag.getDayDropProps(dateParam);
  const metricDropClass = itemDrag.isDayDropTarget(dateParam) ? ' calendar-day-row-wrap--metric-drag' : '';

  const fileDragClass = fileDrop.isFileDragOver ? ' calendar-day-row-wrap--file-drag' : '';
  const fileDragEmptyClass = fileDrop.isFileDragOver ? ' calendar-day-empty--file-drag' : '';

  const closeSheet = useCallback(() => setActionTarget(null), []);

  const handleDelete = useCallback(
    async (target: CalendarActionTarget) => {
      closeSheet();

      if (target.kind === 'metric') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteMetricTitle', 'Delete metric'),
          message: t('calendar.confirm.deleteMetricMessage', 'Delete this metric entry?'),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteMetric(target.uniqueCode);
          showToast(t('calendar.toast.metricDeleted', 'Metric deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.metricDeleteFailed', 'Failed to delete metric'));
        }
        return;
      }

      if (target.kind === 'health_document') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteHealthDocumentTitle', 'Delete health report'),
          message: t(
            'calendar.confirm.deleteHealthDocumentMessage',
            'Delete this report, its file, and all metrics saved from it?',
          ),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await healthDocuments.destroy(target.id);
          showToast(t('calendar.toast.healthDocumentDeleted', 'Health report deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.healthDocumentDeleteFailed', 'Failed to delete health report'));
        }
        return;
      }

      if (target.kind === 'event') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteEventTitle', 'Delete event'),
          message: t('calendar.confirm.deleteEventMessage', { name: target.label }),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteEvent(target.id);
          showToast(t('calendar.toast.eventDeleted', { name: target.label }));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.eventDeleteFailed', 'Failed to delete event'));
        }
        return;
      }

      if (target.kind === 'note') {
        const ok = await confirm({
          title: t('calendar.actionSheet.deleteNote', 'Delete note'),
          message: t('calendar.confirm.deleteNoteMessage', 'Delete this note?'),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteNote(target.id);
          showToast(t('calendar.toast.noteDeleted', 'Note deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.noteDeleteFailed', 'Failed to delete note'));
        }
        return;
      }

      const ok = await confirm({
        title: t('calendar.confirm.deleteWorkoutTitle', 'Delete workout'),
        message: t('calendar.confirm.deleteWorkout', { title: target.title }),
        variant: 'danger',
      });
      if (!ok || !userId) return;

      try {
        await calendarActions.deleteWorkout(userId, target.id);
        showToast(t('calendar.toast.workoutDeleted', { title: target.title }));
        await onReload?.();
      } catch {
        showToast(t('calendar.toast.workoutDeleteFailed', 'Failed to delete workout'));
      }
    },
    [closeSheet, confirm, onReload, showToast, t, userId],
  );

  const handleEdit = useCallback(
    (target: CalendarActionTarget) => {
      if (target.kind === 'metric') {
        navigate(`/calendar/metric/new?date=${target.dateParam}&code=${encodeURIComponent(target.uniqueCode)}`);
        return;
      }
      if (target.kind === 'workout') {
        onWorkoutPress?.({ id: target.id, title: target.title } as CalendarWorkout);
      }
    },
    [navigate, onWorkoutPress],
  );

  const hasContent =
    extra &&
    ((extra.workouts?.length ?? 0) > 0 ||
      (extra.events?.length ?? 0) > 0 ||
      (extra.userMetrics?.length ?? 0) > 0 ||
      (extra.healthDocuments?.length ?? 0) > 0 ||
      extra.today ||
      (extra.notes?.length ?? 0) > 0);

  const goAdd = () => navigate(`/calendar/workout/new?date=${dateParam}`);

  if (!hasContent) {
    return (
      <button
        type="button"
        className={`calendar-day-empty${fileDragEmptyClass}${metricDropClass}`}
        id={`day-${dateKey}`}
        data-calendar-date={dateParam}
        onClick={goAdd}
        onDragOver={(e) => {
          fileDrop.onDragOver(e);
          metricDropProps.onDragOver(e);
        }}
        onDragLeave={(e) => {
          fileDrop.onDragLeave(e);
          metricDropProps.onDragLeave(e);
        }}
        onDrop={(e) => {
          if (isOsFileDrag(e)) {
            fileDrop.onDrop(e);
            return;
          }
          void metricDropProps.onDrop(e);
        }}
      >
        {fileDrop.isFileDragOver ? (
          <span className="calendar-day-empty__drop-hint">{t('calendar.dropHint.file')}</span>
        ) : itemDrag.isDayDropTarget(dateParam) ? (
          <span className="calendar-day-empty__drop-hint">
            {t('calendar.dropHint.item', 'Drop here')}
          </span>
        ) : (
          <>
            {timelineMarker}
            <CalendarDayAddButton decorative className="calendar-day-empty__add" />
            <span className="calendar-day-empty__label">{displayDigits(dayHeaderLabel)}</span>
          </>
        )}
      </button>
    );
  }

  const workoutCount = extra?.workouts?.length ?? 0;
  const workoutTss = getWorkoutTssTotal(extra?.workouts ?? []);
  const workoutItems: NmDayWorkoutItem[] = (extra?.workouts ?? []).map((w) => {
    const actualTime = w.total_time != null ? Number(w.total_time) : null;
    const actualTss = w.tss_actual != null ? Number(w.tss_actual) : null;
    const metrics = buildWorkoutRowMetrics(w);

    const structure = w.structure as { structure?: unknown[] } | null | undefined;

    return {
      id: String(w.id),
      sport: w.workout_type?.name ?? w.workout_type?.english_name ?? 'Workout',
      typeId: w.workout_type?.id,
      sportEnglishName: w.workout_type?.english_name ?? w.workout_type?.name,
      title: w.title ?? w.workout_type?.name ?? t('calendar.workout', 'Workout'),
      metrics,
      done: !!(actualTime || actualTss),
      accentColor: getWorkoutColorSelect(w),
      structurePreview:
        (structure?.structure?.length ?? 0) > 0 ? (
          <WorkoutStructureBarMini structure={w.structure as { structure?: import('../../utils/workoutStructure').ApiStructBlock[] } | null} />
        ) : null,
      onPress: () => onWorkoutPress?.(w),
      onLongPress: () => {
        setActionTarget({
          kind: 'workout',
          id: w.id,
          title: w.title ?? w.workout_type?.name ?? t('calendar.workout', 'Workout'),
          dateParam,
        });
      },
    };
  });

  return (
    <>
      <div
        className={`calendar-day-row-wrap${fileDragClass}${metricDropClass}`}
        id={`day-${dateKey}`}
        data-date-key={dateKey}
        data-calendar-date={dateParam}
        onDragOver={(e) => {
          fileDrop.onDragOver(e);
          metricDropProps.onDragOver(e);
        }}
        onDragLeave={(e) => {
          fileDrop.onDragLeave(e);
          metricDropProps.onDragLeave(e);
        }}
        onDrop={(e) => {
          if (isOsFileDrag(e)) {
            fileDrop.onDrop(e);
            return;
          }
          void metricDropProps.onDrop(e);
        }}
      >
        {fileDrop.isFileDragOver ? (
          <div className="calendar-day-row-wrap__drop-hint" aria-hidden>
            {t('calendar.dropHint.file')}
          </div>
        ) : itemDrag.isDayDropTarget(dateParam) ? (
          <div className="calendar-day-row-wrap__drop-hint" aria-hidden>
            {t('calendar.dropHint.item', 'Drop here')}
          </div>
        ) : null}
        <div className="calendar-day-row__header">
          {timelineMarker}
          <CalendarDayAddButton
            className="calendar-day-row__add"
            onClick={goAdd}
            aria-label={t('calendar.dayAction.addTitle')}
          />
          <div className="calendar-day-row__header-info">
            <span className="calendar-day-row__date">{displayDigits(dayHeaderLabel)}</span>
            {workoutCount > 0 && (
              <span className="calendar-day-row__summary">
                <span>{displayDigits(t('library.addToLibrary.workoutCount', {
                  count: workoutCount,
                  plural: workoutCount === 1 ? '' : 's',
                }))}</span>
                {workoutTss > 0 && <span> · <bdi dir="ltr">{displayDigits(Math.round(workoutTss))} TSS</bdi></span>}
              </span>
            )}
          </div>
        </div>

        {extra?.today && isToday ? (
          <CalendarReadinessWidget
            today={extra.today as unknown as Record<string, unknown>}
            compact
            inline
          />
        ) : null}

        {(extra?.notes ?? []).map((note) => (
          <CalendarNoteRow
            key={note.id}
            content={note.content} title={note.title} description={note.description}
            dragProps={itemDrag.getNoteDragProps(note.id, dateParam, note.title ?? note.content ?? t('calendar.noteCard.label'))}
            onPress={() => navigate(`/calendar/note/new?date=${dateParam}&id=${note.id}`)}
            onLongPress={() => {
              setActionTarget({
                kind: 'note',
                id: note.id,
                label: note.title ?? note.content ?? t('calendar.noteCard.label'),
                dateParam,
              });
            }}
          />
        ))}

        {(extra?.events ?? []).map((ev) => {
          const eventTime = new Date(ev.event_date);
          const future = new Date() < eventTime;
          const untilEvent =
            future
              ? `${Math.floor((eventTime.getTime() - Date.now()) / 86400000)} روز دیگر مانده`
              : 'تمام شد';
          return (
            <CalendarEventRow
              key={ev.id}
              event={ev}
              future={future}
              untilEvent={untilEvent}
              monthLabel={jalaliMonths[item.month]}
              dayLabel={item.day}
              dragProps={itemDrag.getEventDragProps(ev.id, dateParam, ev.name)}
              onPress={() =>
                navigate(`/calendar/event/new?date=${dateParam}&id=${ev.id}`, { state: { event: ev } })
              }
              onLongPress={() => {
                setActionTarget({
                  kind: 'event',
                  id: ev.id,
                  label: ev.name,
                  dateParam,
                });
              }}
            />
          );
        })}

        {(extra?.healthDocuments ?? []).map((doc) => (
          <CalendarHealthDocumentRow
            key={doc.id}
            doc={doc}
            dragProps={itemDrag.getHealthDocumentDragProps(
              doc.id,
              dateParam,
              doc.original_filename,
              doc.unique_code,
            )}
            onDeleted={() => void onReload?.()}
            onLongPress={() => {
              setActionTarget({
                kind: 'health_document',
                id: doc.id,
                label: doc.original_filename,
                uniqueCode: doc.unique_code,
                dateParam,
              });
            }}
          />
        ))}

        {(extra?.userMetrics ?? []).map((m, idx) => {
          const metric = m as {
            unique_code?: string;
            created_at?: string;
            metric_type_name?: string;
            value?: string | number;
            item_count?: number;
          };
          const label = `${metric.metric_type_name ?? 'Metric'}: ${metric.value ?? ''}`;
          return (
            <CalendarMetricRow
              key={metric.unique_code ?? idx}
              metric={metric}
              dragProps={
                metric.unique_code
                  ? itemDrag.getMetricDragProps(metric.unique_code, dateParam, label)
                  : undefined
              }
              onPress={() => {
                if (!metric.unique_code) {
                  navigate(`/calendar/metric/new?date=${dateParam}`);
                  return;
                }
                navigate(`/calendar/metric/new?date=${dateParam}&code=${encodeURIComponent(metric.unique_code)}`);
              }}
              onLongPress={() => {
                if (!metric.unique_code) return;
                setActionTarget({
                  kind: 'metric',
                  uniqueCode: metric.unique_code,
                  label,
                  dateParam,
                });
              }}
            />
          );
        })}

        {(extra?.workouts?.length ?? 0) > 0 && (
          <NmDayWorkoutList items={workoutItems} showDate={false} className="nm-day-workouts--dense" />
        )}
      </div>

      <CalendarItemActionSheet
        target={actionTarget}
        onClose={closeSheet}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onMove={requestMove}
      />

      {movePicker}

      {fileDrop.uploadProgress && <CalendarUploadOverlay progress={fileDrop.uploadProgress} />}
    </>
  );
}
